import { __extends } from "tslib";
import { Vector3D, AbstractionBase, } from '@awayjs/core';
import { ContextGLBlendFactor, ContextGLCompareMode, ContextGLStencilAction, ContextGLTriangleFace, StageEvent, ContextGLClearMask, } from '@awayjs/stage';
import { ViewEvent, } from '@awayjs/view';
import { RenderGroup } from './RenderGroup';
import { RenderableMergeSort } from './sort/RenderableMergeSort';
/**
 * RendererBase forms an abstract base class for classes that are used in the rendering pipeline to render the
 * contents of a partition
 *
 * @class away.render.RendererBase
 */
var RendererBase = /** @class */ (function (_super) {
    __extends(RendererBase, _super);
    /**
     * Creates a new RendererBase object.
     */
    function RendererBase(partition, pool) {
        var _this = _super.call(this, partition, pool) || this;
        _this._cameraForward = new Vector3D();
        _this._depthTextureDirty = true;
        _this._depthPrepass = false;
        _this._pNumElements = 0;
        _this._disableColor = false;
        _this._disableClear = false;
        _this._renderBlended = true;
        _this._numCullPlanes = 0;
        _this._partition = partition;
        _this._maskId = partition.rootNode.getMaskId();
        _this._onSizeInvalidateDelegate = function (event) { return _this.onSizeInvalidate(event); };
        _this._onContextUpdateDelegate = function (event) { return _this.onContextUpdate(event); };
        //default sorting algorithm
        _this.renderableSorter = new RenderableMergeSort();
        _this._renderGroup = pool.renderGroup;
        _this._renderGroup.addEventListener(StageEvent.CONTEXT_CREATED, _this._onContextUpdateDelegate);
        _this._renderGroup.addEventListener(StageEvent.CONTEXT_RECREATED, _this._onContextUpdateDelegate);
        _this._renderGroup.addEventListener(ViewEvent.INVALIDATE_SIZE, _this._onSizeInvalidateDelegate);
        _this._view = _this._renderGroup.view;
        _this._stage = _this._renderGroup.stage;
        if (_this._stage.context)
            _this._context = _this._stage.context;
        return _this;
    }
    Object.defineProperty(RendererBase.prototype, "partition", {
        get: function () {
            return this._partition;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "cullPlanes", {
        /**
         *
         */
        get: function () {
            return this._customCullPlanes;
        },
        set: function (value) {
            this._customCullPlanes = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "renderBlended", {
        get: function () {
            return this._renderBlended;
        },
        set: function (value) {
            this._renderBlended = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "disableColor", {
        get: function () {
            return this._disableColor;
        },
        set: function (value) {
            if (this._disableColor == value)
                return;
            this._disableColor = value;
            this._invalid = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "disableClear", {
        get: function () {
            return this._disableClear;
        },
        set: function (value) {
            if (this._disableClear == value)
                return;
            this._disableClear = value;
            this._invalid = true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "numElements", {
        /**
         *
         */
        get: function () {
            return this._pNumElements;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "context", {
        get: function () {
            return this._context;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "stage", {
        /**
         * The Stage that will provide the ContextGL used for rendering.
         */
        get: function () {
            return this._stage;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RendererBase.prototype, "view", {
        get: function () {
            return this._view;
        },
        enumerable: true,
        configurable: true
    });
    RendererBase.prototype.onClear = function (event) {
        _super.prototype.onClear.call(this, event);
        this._renderGroup.removeEventListener(StageEvent.CONTEXT_CREATED, this._onContextUpdateDelegate);
        this._renderGroup.removeEventListener(StageEvent.CONTEXT_RECREATED, this._onContextUpdateDelegate);
        this._renderGroup.removeEventListener(ViewEvent.INVALIDATE_SIZE, this._onSizeInvalidateDelegate);
        this._onContextUpdateDelegate = null;
        this._onSizeInvalidateDelegate = null;
        this._view = null;
        this._stage = null;
        this._context = null;
    };
    /**
     * Renders the potentially visible geometry to the back buffer or texture.
     * @param target An option target texture to render to.
     * @param surfaceSelector The index of a CubeTexture's face to render to.
     * @param additionalClearMask Additional clear mask information, in case extra clear channels are to be omitted.
     */
    RendererBase.prototype.render = function (enableDepthAndStencil, surfaceSelector, mipmapSelector, maskConfig) {
        //TODO refactor setTarget so that rendertextures are created before this check
        // if (!this._stage || !this._context)
        // 	return;
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        if (maskConfig === void 0) { maskConfig = 0; }
        this._enableDepthAndStencil = enableDepthAndStencil;
        this._surfaceSelector = surfaceSelector;
        this._mipmapSelector = mipmapSelector;
        this._maskConfig = maskConfig;
        //check for mask rendering
        if (this._maskConfig) {
            this._disableClear = true;
            this._disableColor = true;
        }
        this._renderGroup.update(this._partition);
        // invalidate mipmaps (if target exists) to regenerate if required
        if (this._view.target)
            this._view.target.invalidateMipmaps();
        // this._pRttViewProjectionMatrix.copyFrom(projection.viewMatrix3D);
        // this._pRttViewProjectionMatrix.appendScale(this.textureRatioX, this.textureRatioY, 1);
        //TODO: allow sharedContexts for image targets
        this._view.clear(!this._depthPrepass && !this._disableClear, enableDepthAndStencil, surfaceSelector, mipmapSelector, (!this._view.shareContext || this._view.target)
            ? ContextGLClearMask.ALL
            : ContextGLClearMask.DEPTH);
        /*
         if (_backgroundImageRenderer)
         _backgroundImageRenderer.render();
         */
        //initialise blend mode
        this._context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ZERO);
        //initialise depth test
        this._context.setDepthTest(true, ContextGLCompareMode.LESS_EQUAL);
        this.executeRender(enableDepthAndStencil, surfaceSelector, mipmapSelector);
        //line required for correct rendering when using away3d with starling.
        //DO NOT REMOVE UNLESS STARLING INTEGRATION IS RETESTED!
        //this._context.setDepthTest(false, ContextGLCompareMode.LESS_EQUAL); //oopsie
        if (!this._view.shareContext || this._view.target) {
            if (this._snapshotRequired && this._snapshotBitmapImage2D) {
                this._context.drawToBitmapImage2D(this._snapshotBitmapImage2D);
                this._snapshotRequired = false;
            }
        }
    };
    RendererBase.prototype.traverse = function () {
        //reset head values
        this._pBlendedRenderableHead = null;
        this._pOpaqueRenderableHead = null;
        this._pNumElements = 0;
        this._activeMaskOwners = null;
        this._cameraTransform = this._view.projection.transform.matrix3D;
        this._cameraForward = this._view.projection.transform.forwardVector;
        this._cullPlanes = this._customCullPlanes ? this._customCullPlanes : this._view.projection.viewFrustumPlanes;
        this._numCullPlanes = this._cullPlanes ? this._cullPlanes.length : 0;
        RendererBase._collectionMark++;
        this._partition.traverse(this);
        //sort the resulting renderables
        if (this.renderableSorter) {
            this._pOpaqueRenderableHead = this.renderableSorter.sortOpaqueRenderables(this._pOpaqueRenderableHead);
            this._pBlendedRenderableHead = this.renderableSorter.sortBlendedRenderables(this._pBlendedRenderableHead);
        }
        this._invalid = false;
    };
    RendererBase.prototype._iRenderCascades = function (projection, node, enableDepthAndStencil, surfaceSelector) {
        // this._stage.setRenderTarget(target, true, 0);
        // this._context.clear(1, 1, 1, 1, 1, 0);
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        // this._context.setBlendFactors(ContextGLBlendFactor.ONE, ContextGLBlendFactor.ZERO);
        // this._context.setDepthTest(true, ContextGLCompareMode.LESS);
        // var head:_Render_RenderableBase = this._pOpaqueRenderableHead;
        // var first:boolean = true;
        // //TODO cascades must have separate collectors, rather than separate draw commands
        // for (var i:number = numCascades - 1; i >= 0; --i) {
        // 	//this._stage.scissorRect = scissorRects[i];
        // 	//this.drawCascadeRenderables(head, cameras[i], first? null : cameras[i].frustumPlanes);
        // 	first = false;
        // }
        // line required for correct rendering when using away3d with starling.
        // DO NOT REMOVE UNLESS STARLING INTEGRATION IS RETESTED!
        // this._context.setDepthTest(false, ContextGLCompareMode.LESS_EQUAL);
    };
    /**
     * Renders the potentially visible geometry to the back buffer or texture. Only executed if everything is set up.
     *
     * @param target An option target texture to render to.
     * @param surfaceSelector The index of a CubeTexture's face to render to.
     * @param additionalClearMask Additional clear mask information, in case extra clear channels are to be omitted.
     */
    RendererBase.prototype.executeRender = function (enableDepthAndStencil, surfaceSelector, mipmapSelector) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = true; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        //if (this._invalid)
        this.traverse();
        //initialise color mask
        if (this._disableColor)
            this._context.setColorMask(false, false, false, false);
        else
            this._context.setColorMask(true, true, true, true);
        //initialise stencil
        if (this._maskConfig)
            this._context.enableStencil();
        else
            this._context.disableStencil();
        this.drawRenderables(this._pOpaqueRenderableHead);
        if (this._renderBlended)
            this.drawRenderables(this._pBlendedRenderableHead);
    };
    RendererBase.prototype.onInvalidate = function (event) {
        _super.prototype.onInvalidate.call(this, event);
        this._renderGroup.invalidate();
    };
    /*
     * Will draw the renderer's output on next render to the provided bitmap data.
     * */
    RendererBase.prototype.queueSnapshot = function (bmd) {
        this._snapshotRequired = true;
        this._snapshotBitmapImage2D = bmd;
    };
    //private drawCascadeRenderables(renderRenderable:_Render_RenderableBase, camera:Camera, cullPlanes:Array<Plane3D>)
    //{
    //	var renderRenderable2:_Render_RenderableBase;
    //	var render:_Render_MaterialBase;
    //	var pass:IPass;
    //
    //	while (renderRenderable) {
    //		renderRenderable2 = renderRenderable;
    //		render = renderRenderable.render;
    //		pass = render.passes[0] //assuming only one pass per material
    //
    //		this.activatePass(renderRenderable, pass, camera);
    //
    //		do {
    //			// if completely in front, it will fall in a different cascade
    //			// do not use near and far planes
    //			if (!cullPlanes || renderRenderable2.node.worldBounds.isInFrustum(cullPlanes, 4)) {
    //				renderRenderable2._iRender(pass, camera, this._pRttViewProjectionMatrix);
    //			} else {
    //				renderRenderable2.cascaded = true;
    //			}
    //
    //			renderRenderable2 = renderRenderable2.next;
    //
    //		} while (renderRenderable2 && renderRenderable2.render == render && !renderRenderable2.cascaded);
    //
    //		this.deactivatePass(renderRenderable, pass);
    //
    //		renderRenderable = renderRenderable2;
    //	}
    //}
    /**
     * Draw a list of renderables.
     *
     * @param renderables The renderables to draw.
     */
    RendererBase.prototype.drawRenderables = function (renderRenderable) {
        var i;
        var r;
        var renderMaterial;
        var numPasses;
        while (renderRenderable) {
            renderMaterial = renderRenderable.renderMaterial;
            numPasses = renderMaterial ? renderMaterial.numPasses : 1;
            if (this._activeMasksDirty || this._checkMaskOwners(renderRenderable.maskOwners)) {
                if (!(this._activeMaskOwners = renderRenderable.maskOwners)) {
                    //re-establish stencil settings (if not inside another mask)
                    if (!this._maskConfig)
                        this._context.disableStencil();
                }
                else {
                    this._renderMasks(this._activeMaskOwners);
                }
                this._activeMasksDirty = false;
            }
            //iterate through each shader object
            for (i = 0; i < numPasses; i++) {
                renderMaterial && renderMaterial.activatePass(i);
                r = renderRenderable;
                do {
                    ///console.log("maskOwners", renderRenderable2.maskOwners);
                    r.executeRender(this._enableDepthAndStencil, this._surfaceSelector, this._mipmapSelector, this._maskConfig);
                    r = r.next;
                } while (r
                    && r.renderMaterial == renderMaterial
                    && !(this._activeMasksDirty = this._checkMaskOwners(r.maskOwners)));
                renderMaterial && renderMaterial.deactivatePass();
            }
            renderRenderable = r;
        }
    };
    /**
     * Assign the context once retrieved
     */
    RendererBase.prototype.onContextUpdate = function (event) {
        this._context = this._stage.context;
    };
    /*
     public get iBackground():Texture2DBase
     {
     return this._background;
     }
     */
    /*
     public set iBackground(value:Texture2DBase)
     {
     if (this._backgroundImageRenderer && !value) {
     this._backgroundImageRenderer.dispose();
     this._backgroundImageRenderer = null;
     }

     if (!this._backgroundImageRenderer && value)
     {

     this._backgroundImageRenderer = new BackgroundImageRenderer(this._stage);

     }

     this._background = value;

     if (this._backgroundImageRenderer)
     this._backgroundImageRenderer.texture = value;
     }
     */
    /*
     public get backgroundImageRenderer():BackgroundImageRenderer
     {
     return _backgroundImageRenderer;
     }
     */
    /**
     *
     */
    RendererBase.prototype.onSizeInvalidate = function (event) {
        if (this._pRttBufferManager) {
            this._pRttBufferManager.viewWidth = this._view.width;
            this._pRttBufferManager.viewHeight = this._view.height;
        }
        this._depthTextureDirty = true;
    };
    /**
     *
     * @param node
     * @returns {boolean}
     */
    RendererBase.prototype.enterNode = function (node) {
        var maskOrFrustrum = (this._maskConfig
            || node.isInFrustum(this._partition.rootNode, this._cullPlanes, this._numCullPlanes, this._renderGroup.pickGroup));
        var enter = node._collectionMark != RendererBase._collectionMark
            && node.isRenderable()
            && maskOrFrustrum
            && node.getMaskId() == this._maskId;
        node._collectionMark = RendererBase._collectionMark;
        return enter;
    };
    RendererBase.prototype.getTraverser = function (partition) {
        //if (false) {
        if (partition.rootNode.renderToImage) {
            //new node for the container
            var node = partition.getLocalNode();
            var renderGroup = RenderGroup.getInstance(partition.getLocalView(this._stage), this._traverserClass);
            var boundsPicker = renderGroup.pickGroup.getBoundsPicker(node.partition);
            if (!boundsPicker.getBoxBounds(node, true, true))
                return this;
            var traverser = renderGroup.getRenderer(node.partition);
            traverser.rootView = this._view;
            traverser.renderableSorter = null;
            //if (this._invalid) {
            this._renderEntity = traverser;
            // project onto camera's z-axis
            this._zIndex = this._cameraTransform.position.subtract(partition.rootNode.getPosition())
                .dotProduct(this._cameraForward)
                + partition.rootNode.container.zOffset;
            //save sceneTransform
            this._renderSceneTransform = partition.rootNode.getRenderMatrix3D(this._cameraTransform);
            //save mask id
            this._entityMaskId = partition.rootNode.getMaskId();
            this._entityMaskOwners = partition.rootNode.getMaskOwners();
            this.applyTraversable(traverser);
            //}
            return traverser;
        }
        return this;
    };
    RendererBase.prototype.applyEntity = function (node) {
        this._renderEntity = node.getAbstraction(this._renderGroup);
        // project onto camera's z-axis
        this._zIndex = this._cameraTransform.position.subtract(node.parent.getPosition())
            .dotProduct(this._cameraForward)
            + node.entity.zOffset;
        //save sceneTransform
        this._renderSceneTransform = node.parent.getRenderMatrix3D(this._cameraTransform);
        //save mask id
        this._entityMaskId = node.parent.getMaskId();
        this._entityMaskOwners = node.parent.getMaskOwners();
        //collect renderables
        node.entity._acceptTraverser(this);
    };
    RendererBase.prototype.applyTraversable = function (traversable) {
        var renderRenderable = traversable.getAbstraction(this._renderEntity);
        //store renderable properties
        renderRenderable.renderGroup = this._renderGroup;
        renderRenderable.cascaded = false;
        renderRenderable.zIndex = this._zIndex;
        renderRenderable.maskId = this._entityMaskId;
        renderRenderable.maskOwners = this._entityMaskOwners;
        renderRenderable.renderSceneTransform = this._renderSceneTransform;
        var renderMaterial = renderRenderable.renderMaterial;
        renderRenderable.materialID = renderMaterial.materialID;
        renderRenderable.renderOrderId = renderMaterial.renderOrderId;
        if (renderMaterial.requiresBlending) {
            renderRenderable.next = this._pBlendedRenderableHead;
            this._pBlendedRenderableHead = renderRenderable;
        }
        else {
            renderRenderable.next = this._pOpaqueRenderableHead;
            this._pOpaqueRenderableHead = renderRenderable;
        }
        //need to re-trigger stageElements getter in case animator has changed
        this._pNumElements += renderRenderable.stageElements.elements.numElements;
    };
    RendererBase.prototype._renderMasks = function (maskOwners) {
        //calculate the bit index of maskConfig devided by two
        var halfBitIndex = Math.log2(this._maskConfig) >> 1;
        //create a new base and config value for the mask to be rendered
        //maskBase set to next odd significant bit
        var newMaskBase = this._maskConfig ? Math.pow(2, (halfBitIndex + 1) << 1) : 1;
        var newMaskConfig = newMaskBase;
        if (newMaskConfig > 0xff) {
            console.warn('[RenderBase] Mask bit overflow, maskConfig %d', newMaskConfig);
            return;
        }
        this._context.enableStencil();
        var numLayers = maskOwners.length;
        var children;
        var numChildren;
        var mask;
        var first = true;
        for (var i = 0; i < numLayers; ++i) {
            children = maskOwners[i].getMasks();
            numChildren = children.length;
            if (numChildren) {
                this._context.setStencilActions(ContextGLTriangleFace.FRONT_AND_BACK, (first) ? ContextGLCompareMode.ALWAYS
                    : ContextGLCompareMode.EQUAL, ContextGLStencilAction.SET, ContextGLStencilAction.SET, ContextGLStencilAction.KEEP);
                first = false;
                //flips between read odd write even to read even write odd
                this._context.setStencilReferenceValue(0xFF, newMaskConfig, newMaskConfig = (newMaskConfig & newMaskBase) + newMaskBase);
                //clears write mask to zero
                this._context.clear(0, 0, 0, 0, 0, 0, ContextGLClearMask.STENCIL);
                for (var j = 0; j < numChildren; ++j) {
                    mask = children[j];
                    //todo: figure out why masks can be null here
                    if (mask)
                        this._renderGroup.getRenderer(mask.partition).render(true, 0, 0, newMaskConfig);
                }
            }
        }
        if (!first) {
            this._context.setStencilActions(ContextGLTriangleFace.FRONT_AND_BACK, ContextGLCompareMode.EQUAL, ContextGLStencilAction.SET, ContextGLStencilAction.SET, ContextGLStencilAction.KEEP);
        }
        //reads from mask output, writes to previous mask state
        this._context.setStencilReferenceValue(0xFF, newMaskConfig, this._maskConfig);
        //re-establish color mask settings (if not inside another mask)
        if (!this._disableColor) {
            this._context.setColorMask(true, true, true, true);
        }
    };
    RendererBase.prototype._checkMaskOwners = function (maskOwners) {
        if (this._activeMaskOwners == null || maskOwners == null)
            return Boolean(this._activeMaskOwners != maskOwners);
        if (this._activeMaskOwners.length != maskOwners.length)
            return true;
        var numLayers = maskOwners.length;
        var numMasks;
        var masks;
        var activeNumMasks;
        var activeMasks;
        for (var i = 0; i < numLayers; i++) {
            masks = maskOwners[i].getMasks();
            numMasks = masks.length;
            activeMasks = this._activeMaskOwners[i].getMasks();
            activeNumMasks = activeMasks.length;
            if (activeNumMasks != numMasks)
                return true;
            for (var j = 0; j < numMasks; j++) {
                if (activeMasks[j] != masks[j])
                    return true;
            }
        }
        return false;
    };
    RendererBase._collectionMark = 0;
    return RendererBase;
}(AbstractionBase));
export { RendererBase };
